import { useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import productionHubLogo from "@assets/Production_Hub_1771339483036.png";
import bgVideo from "@assets/Production_1771479229515.mp4";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    setIsPending(true);
    try {
      await apiRequest("POST", "/api/auth/login", { username: username.trim(), password });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      navigate("/", { replace: true });
    } catch (err: any) {
      const msg = err.message?.includes("401")
        ? "Invalid username or password"
        : "Login failed. Please try again.";
      toast({ title: "Login failed", description: msg, variant: "destructive", severity: "error" });
    } finally {
      setIsPending(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;

    setIsPending(true);
    try {
      await apiRequest("POST", "/api/auth/forgot-password", { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch {
      // Always show success to prevent email enumeration
      setForgotSent(true);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: "hsl(var(--brand-dark))" }}>
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: 0.9 }}
      >
        <source src={bgVideo} type="video/mp4" />
      </video>
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 54, 58, 0.55)" }} />
      <div className="w-full max-w-sm px-4 relative z-10">
        <Card data-testid="card-login" style={{ backgroundColor: "hsl(var(--brand-dark))", border: "none" }}>
          <CardHeader className="pb-4 flex flex-col items-center gap-4">
            <img
              src={productionHubLogo}
              alt="Prodigy Production Hub"
              className="w-auto"
              style={{ height: "3.85rem" }}
            />
            <h2 className="text-lg font-semibold text-center text-white" data-testid="text-login-title">
              {showForgot ? "Forgot Password" : "Sign In"}
            </h2>
          </CardHeader>
          <CardContent>
            {showForgot ? (
              forgotSent ? (
                <div className="text-center space-y-4">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-[hsl(var(--brand-green))]" />
                  <p className="text-sm text-gray-300">
                    If an account with that email exists, a password reset link has been sent. Check your inbox.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-gray-300">
                    Enter the email address linked to your account. We'll send you a link to reset your password.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="forgotEmail" className="text-white">Email</Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isPending || !forgotEmail.trim()}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setShowForgot(false); setForgotEmail(""); }}
                      className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Back to Sign In
                    </button>
                  </div>
                </form>
              )
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-white">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                    autoFocus
                    data-testid="input-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    data-testid="input-password"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isPending || !username.trim() || !password.trim()}
                  data-testid="button-login"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
